import type { Completion, CompletionSource } from '@codemirror/autocomplete'

/**
 * Variables and placeholders that can be used in ad code — both in layout render
 * templates (Handlebars) and in third-party creative code (bracket/Google macros).
 */

export interface AdCodeVariable {
  name: string
  description: string
  example?: string
}

export interface AdCodeVariableGroup {
  label: string
  /** How the variable is written in code. */
  syntax: 'handlebars' | 'literal'
  vars: AdCodeVariable[]
}

// ── Handlebars variables (layout render templates) ──────────────────────────

/**
 * The variables Adnuntius substitutes into any creative code component. This is the
 * only Handlebars set offered on a creative — the richer set below applies to layout
 * render templates, where the component tags are actually known.
 */
export const GENERAL_VARIABLE_GROUP: AdCodeVariableGroup = {
  label: 'General variables',
  syntax: 'handlebars',
  vars: [
    { name: 'auId', description: 'The ad unit tag ID from which the ad is served.' },
    { name: 'adId', description: 'A unique ID for the ad that is delivered. Can be used as a cachebuster or random number.' },
    { name: 'responseId', description: 'The ID of the container for the entire ad unit response.' },
    { name: 'matchedAdCount', description: 'The number of ads returned across the entire ad unit response.' },
    { name: 'lineItemId', description: 'ID of the line item that is delivered.' },
    { name: 'creativeId', description: 'ID of the creative that is delivered.' },
    { name: 'creativeWidth', description: 'The width specified on the creative.' },
    { name: 'creativeHeight', description: 'The height specified on the creative.' },
    { name: 'advertiser.legalName', description: 'The legal name of the advertiser whose ad appears.' },
    { name: 'referer', description: 'The URL from which the ad request was sent.' },
    { name: 'context', description: 'If specified, the context URL sent with the ad request. Otherwise the same as referer.' },
    { name: 'vastVersion', description: 'The VAST version. Available in VAST video layouts.' },
    { name: 'gdpr', description: 'A flag 0 or 1 to indicate if GDPR is applicable for this impression.' },
    { name: 'euconsent', description: 'The contents of the euconsent cookie if present.' },
  ],
}

export const HANDLEBARS_VARIABLE_GROUPS: AdCodeVariableGroup[] = [
  {
    label: 'General',
    syntax: 'handlebars',
    vars: [
      { name: 'auId', description: 'The ad unit tag ID from which the ad is served.' },
      { name: 'adId', description: 'A unique ID for the ad. Can be used as a cachebuster.' },
      { name: 'responseId', description: 'The ID of the container for the entire ad unit response.' },
      { name: 'matchedAdCount', description: 'The number of ads returned across the entire ad unit response.' },
      { name: 'lineItemId', description: 'ID of the line item that is delivered.' },
      { name: 'creativeId', description: 'ID of the creative that is delivered.' },
      { name: 'creativeWidth', description: 'The width specified on the creative.' },
      { name: 'creativeHeight', description: 'The height specified on the creative.' },
      { name: 'selectedColumn', description: 'The column for the creative within the ad unit.' },
      { name: 'selectedColumnPosition', description: 'The column position (row) for the creative within the ad unit.' },
      { name: 'advertiser.legalName', description: 'The legal name of the advertiser whose ad appears.' },
      { name: 'referer', description: 'The URL from which the ad request was sent.' },
      { name: 'context', description: 'The context URL sent with the ad request. Otherwise same as referer.' },
      { name: 'cpmAmount', description: 'The line item CPM amount (if specified).' },
      { name: 'cpmCurrency', description: 'The line item CPM currency (if specified).' },
      { name: 'cpcAmount', description: 'The line item CPC amount (if specified).' },
      { name: 'cpcCurrency', description: 'The line item CPC currency (if specified).' },
      { name: 'cpaAmount', description: 'The line item CPA amount (if specified).' },
      { name: 'cpaCurrency', description: 'The line item CPA currency (if specified).' },
      { name: 'bidAmount', description: 'The line item bid amount.' },
      { name: 'bidCurrency', description: 'The line item bid currency.' },
      { name: 'costAmount', description: 'The impression cost (can be zero for CPC/CPA line items).' },
      { name: 'costCurrency', description: 'The impression cost currency.' },
      { name: 'impressionTrackingUrls', description: 'A list of impression tracking URLs supplied for the creative.' },
      { name: 'impressionTrackingUrlsEsc', description: 'URL-escaped versions of the impression trackers.' },
      { name: 'clickUrl', description: 'URL to record a creative click.' },
      { name: 'renderedPixel', description: 'URL to record a rendered impression.' },
      { name: 'renderedPixelEsc', description: 'URL-escaped version of rendered impression URL.' },
      { name: 'visibleUrl', description: 'URL to record a visible impression.' },
      { name: 'visibleUrlEsc', description: 'URL-escaped version of visible impression URL.' },
      { name: 'viewUrl', description: 'URL to record a viewable impression.' },
      { name: 'viewUrlEsc', description: 'URL-escaped version of viewable impression URL.' },
      { name: 'rt', description: 'The response token for sending custom events for this creative.' },
      { name: 'adServerHost', description: 'The ad server host name.' },
      { name: 'vastVersion', description: 'The VAST version. Available in VAST video layouts.' },
      { name: 'gdpr', description: 'Flag 0 or 1 indicating if GDPR is applicable.' },
      { name: 'euconsent', description: 'The contents of the euconsent cookie if present.' },
    ],
  },
  {
    label: 'Asset components (replace TAG with component tag)',
    syntax: 'handlebars',
    vars: [
      { name: 'assets.TAG.cdnId', description: 'The URL of the uploaded asset on the CDN.' },
      { name: 'assets.TAG.previewCdnId', description: 'If an image, the URL of a low-resolution preview.' },
      { name: 'assets.TAG.width', description: 'If an image or video, the asset width.' },
      { name: 'assets.TAG.height', description: 'If an image or video, the asset height.' },
      { name: 'assets.TAG.mimeType', description: 'The mime type of the asset e.g. image/png, video/mp4.' },
      { name: 'assets.TAG.duration', description: 'If a video, the video duration.' },
      { name: 'assets.TAG.bps', description: 'If a video, the bits-per-second of the original uploaded video.' },
      { name: 'assets.TAG.contents', description: 'The contents of the HTML file (HTML Media component).' },
      { name: 'assets.TAG.contentsAsJs', description: 'The contents of the HTML file formatted as a valid JS string.' },
    ],
  },
  {
    label: 'Video transcoding (replace TAG)',
    syntax: 'handlebars',
    vars: [
      { name: 'assets.TAG.transcode.dash.cdnId', description: 'URL of the DASH transcoded output on the CDN.' },
      { name: 'assets.TAG.transcode.dash.bps', description: 'Bits-per-second of the DASH transcoded output.' },
      { name: 'assets.TAG.transcode.dash.mimeType', description: 'Mime type of the DASH transcoded output.' },
      { name: 'assets.TAG.transcode.hls.cdnId', description: 'URL of the HLS transcoded output on the CDN.' },
      { name: 'assets.TAG.transcode.hls.bps', description: 'Bits-per-second of the HLS transcoded output.' },
      { name: 'assets.TAG.transcode.hls.mimeType', description: 'Mime type of the HLS transcoded output.' },
      { name: 'assets.TAG.transcode.mpeg4_270.cdnId', description: 'URL of the 270p MPEG-4 transcoded output.' },
      { name: 'assets.TAG.transcode.mpeg4_432.cdnId', description: 'URL of the 432p MPEG-4 transcoded output.' },
      { name: 'assets.TAG.transcode.mpeg4_576.cdnId', description: 'URL of the 576p MPEG-4 transcoded output.' },
      { name: 'assets.TAG.transcode.mpeg4_720.cdnId', description: 'URL of the 720p MPEG-4 transcoded output.' },
      { name: 'assets.TAG.transcode.quicktime.cdnId', description: 'URL of the QuickTime transcoded output.' },
      { name: 'assets.TAG.transcode.preview.cdnId', description: 'URL of the preview transcoded output.' },
    ],
  },
  {
    label: 'Text components (replace TAG)',
    syntax: 'handlebars',
    vars: [
      { name: 'text.TAG.content', description: 'The text inputted into the creative component.' },
      { name: 'text.TAG.contentAsJs', description: 'The text formatted as a valid JavaScript string.' },
      { name: 'text.TAG.length', description: 'The length of the text.' },
      { name: 'text.TAG.minLength', description: "The minimum length of the component's content." },
      { name: 'text.TAG.maxLength', description: "The maximum length of the component's content." },
    ],
  },
  {
    label: 'URL components (replace TAG)',
    syntax: 'handlebars',
    vars: [
      { name: 'urls.TAG.url', description: 'The Adnuntius-adjusted URL used to count clicks.' },
      { name: 'urls.TAG.urlEsc', description: 'URL-escaped version of the Adnuntius-adjusted URL.' },
      { name: 'urls.TAG.destinationUrl', description: "The raw URL inputted into the creative's component." },
    ],
  },
  {
    label: 'Choice components (replace TAG)',
    syntax: 'handlebars',
    vars: [
      { name: 'choices.TAG.value', description: 'The value of the choice selected in the creative.' },
    ],
  },
  {
    label: 'Key-value variables (replace myKey)',
    syntax: 'handlebars',
    vars: [
      { name: 'kvs.myKey.[0]', description: 'The first value for the key.' },
      { name: 'kvs.myKey.[1]', description: 'The second value for the key.' },
      { name: 'kvsAsJs', description: 'A JSON representation of all key-values specified.' },
    ],
  },
  {
    label: 'Layout includes (replace reference)',
    syntax: 'handlebars',
    vars: [
      { name: 'include.reference', description: 'The render template of the layout include.' },
    ],
  },
  {
    label: 'Custom helpers',
    syntax: 'handlebars',
    vars: [
      {
        name: 'ifEquals',
        description: 'True when a equals b.',
        example: '{{#ifEquals matchedAdCount 0}}\n  <p>No ads found.</p>\n{{else}}\n  <p>Found {{matchedAdCount}} ads.</p>\n{{/ifEquals}}',
      },
      {
        name: 'ifNotEquals',
        description: 'True when a does not equal b.',
        example: '{{#ifNotEquals text.myField.content "default"}}\n  <p>{{text.myField.content}}</p>\n{{else}}\n  <p>Default content here.</p>\n{{/ifNotEquals}}',
      },
      {
        name: 'ifGt',
        description: 'True when a is greater than b.',
        example: '{{#ifGt matchedAdCount 1}}\n  <p>More than 1 ad returned!</p>\n{{else}}\n  <p>1 or 0 ads returned.</p>\n{{/ifGt}}',
      },
      {
        name: 'ifGte',
        description: 'True when a is greater than or equal to b.',
        example: '{{#ifGte matchedAdCount 1}}\n  <p>1 or more ads returned</p>\n{{else}}\n  <p>No ads returned.</p>\n{{/ifGte}}',
      },
      {
        name: 'ifLt',
        description: 'True when a is less than b.',
        example: '{{#ifLt assets.image.width 500}}\n  div { margin-left: 100px; }\n{{else}}\n  div { margin-left: 20px; }\n{{/ifLt}}',
      },
      {
        name: 'ifLte',
        description: 'True when a is less than or equal to b.',
        example: '{{#ifLte assets.image.width 500}}\n  div { margin-left: 100px; }\n{{else}}\n  div { margin-left: 20px; }\n{{/ifLte}}',
      },
    ],
  },
  {
    label: 'Special strings',
    syntax: 'handlebars',
    vars: [
      { name: 'adnOptionContainerDiv', description: 'Add as HTML/JS comment to render in a div tag.' },
      { name: 'adnOptionContainerIframe', description: 'Add as HTML/JS comment to render in an IFrame.' },
    ],
  },
]

// ── Third-party creative placeholders ──────────────────────────────────────

export const THIRD_PARTY_VARIABLE_GROUPS: AdCodeVariableGroup[] = [
  {
    label: 'Adnuntius placeholders',
    syntax: 'literal',
    vars: [
      { name: '[[adnClickUrlPlaceholder]]', description: 'Replaced by the click URL for the ad at the time of ad serving.' },
      { name: '[[adnEscapedClickUrlPlaceholder]]', description: 'Replaced by the URL-escaped click URL for the ad.' },
      { name: '[[adnDoubleEscapedClickUrlPlaceholder]]', description: 'Replaced by the double URL-escaped click URL for the ad.' },
      { name: '[[adnCacheBusterPlaceholder]]', description: 'Replaced by a random set of characters at the time of ad serving.' },
    ],
  },
  {
    label: 'Google macros',
    syntax: 'literal',
    vars: [
      { name: '%%CLICK_URL_UNESC%%', description: 'The unescaped click URL for the ad.' },
      { name: '%%CLICK_URL_ESC%%', description: 'The URL-escaped click URL for the ad.' },
      { name: '%%CLICK_URL_ESC_ESC%%', description: 'The double URL-escaped click URL for the ad.' },
      { name: '%%CACHEBUSTER%%', description: 'A random set of characters used as a cachebuster.' },
    ],
  },
]

// ── Autocomplete sources ───────────────────────────────────────────────────

function toOptions(groups: AdCodeVariableGroup[], type: string): Completion[] {
  return groups.flatMap((group) =>
    group.vars.map((v) => ({ label: v.name, detail: v.description, type }))
  )
}

/** Completes `{{variable}}` / `{{{variable}}}` after typing `{{`. */
function handlebarsCompletionSource(options: Completion[]): CompletionSource {
  return (context) => {
    const match = context.matchBefore(/\{{2,3}[\w.[\]]*/)
    if (!match || (match.from === match.to && !context.explicit)) return null

    const braces = match.text.match(/^(\{{2,3})/)?.[1] ?? '{{'
    const closing = braces === '{{{' ? '}}}' : '}}'

    return {
      from: match.from + braces.length,
      options: options.map((option) => ({
        ...option,
        apply: (view, completion, _from, to) => {
          view.dispatch({
            changes: { from: match.from, to, insert: `${braces}${completion.label}${closing}` },
          })
        },
      })),
      filter: true,
      validFor: /[\w.[\]]*/,
    }
  }
}

/** Completes the full-text placeholders `[[adn…]]` and `%%GOOGLE_MACRO%%`. */
function literalCompletionSource(options: Completion[]): CompletionSource {
  return (context) => {
    const match = context.matchBefore(/(\[{2}|%{2})\w*/)
    if (!match || (match.from === match.to && !context.explicit)) return null

    const prefix = match.text.slice(0, 2)
    const matching = options.filter((option) => String(option.label).startsWith(prefix))
    if (matching.length === 0) return null

    return {
      from: match.from,
      options: matching.map((option) => ({
        ...option,
        apply: (view, completion, _from, to) => {
          view.dispatch({ changes: { from: match.from, to, insert: String(completion.label) } })
        },
      })),
      filter: true,
      validFor: /[\w[\]%]*/,
    }
  }
}

/** Every Handlebars variable — layout render templates only. */
export const layoutTemplateCompletions = handlebarsCompletionSource(
  toOptions(HANDLEBARS_VARIABLE_GROUPS, 'variable')
)

/** The general variables usable in any creative code component. */
export const generalVariableCompletions = handlebarsCompletionSource(
  toOptions([GENERAL_VARIABLE_GROUP], 'variable')
)

/** Click/cachebuster placeholders recognised in third-party creative code. */
export const thirdPartyCompletions = literalCompletionSource(
  toOptions(THIRD_PARTY_VARIABLE_GROUPS, 'constant')
)
